import { createFileRoute, redirect, useRouteContext } from '@tanstack/react-router';
import type { FeatureKey } from '@logspot-platform/platform-types';
import { TabbedPageLayout, type RoutedTab } from '@/components/tabbed-page-layout';
import { useFeatures } from '@/hooks/use-features';
import { useProjects } from '@/hooks/use-projects';
import { hasPermission } from '@/lib/permissions';

export const Route = createFileRoute('/_auth/_side-layout/project-settings')({
  component: ProjectSettingsLayout,
  beforeLoad: async ({ context }) => {
    if (!hasPermission(context.userRole, { project: ['update'] })) {
      throw redirect({ to: '/' });
    }
  },
});

type SettingsTab = {
  to: RoutedTab['to'];
  label: string;
  // Org-admin-only tab. Revenue's config is project-scoped, but connecting
  // Stripe is an org-admin capability on the backend, so the tab is gated to
  // org owners/admins (and filtered out of the tab bar for everyone else).
  adminOnly?: boolean;
  // Launch-gated tab: hidden until the org has this feature flag on.
  feature?: FeatureKey;
};

const TABS: SettingsTab[] = [
  { to: '/project-settings/general', label: 'General' },
  { to: '/project-settings/domains', label: 'Domains' },
  { to: '/project-settings/filters', label: 'Filters' },
  { to: '/project-settings/autocapture', label: 'Autocapture' },
  { to: '/project-settings/revenue', label: 'Revenue', adminOnly: true },
  { to: '/project-settings/consent', label: 'Consent', adminOnly: true },
  { to: '/project-settings/integrations', label: 'Integrations' },
  // Connecting Google is org-admin-gated on the backend, like Revenue's Stripe.
  {
    to: '/project-settings/search-console',
    label: 'Search Console',
    adminOnly: true,
    feature: 'searchConsole',
  },
  { to: '/project-settings/identity', label: 'Identity' },
  { to: '/project-settings/playground', label: 'Playground' },
  { to: '/project-settings/danger-zone', label: 'Danger Zone' },
];

// Named component (not an inline one-liner): the tab list is filtered by role
// and the page waits for the active project name before rendering.
function ProjectSettingsLayout() {
  const { projectName } = useProjects();
  const { userRole } = useRouteContext({ from: '/_auth' });
  const { isEnabled } = useFeatures();
  const isOrgAdmin = hasPermission(userRole, { organization: ['update'] });
  if (!projectName) return null;

  const tabs = TABS.filter(
    (tab) =>
      (!tab.adminOnly || isOrgAdmin) && (!tab.feature || isEnabled(tab.feature)),
  );
  return (
    <TabbedPageLayout
      title="Project Settings"
      description="Configure your project and integrations."
      tabs={tabs}
    />
  );
}
